import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { localized } from "../lib/format";

const HALL_TYPES = [
  { value: "luxury", label: "لوکس" },
  { value: "standard", label: "استندرد" },
  { value: "garden", label: "باغ" },
  { value: "vip_salon", label: "سالون VIP" },
];

const AMENITIES = [
  { value: "parking", label: "پارکینگ" },
  { value: "generator", label: "جنراتور" },
  { value: "bridal_room", label: "اتاق عروس" },
  { value: "vip_room", label: "اتاق VIP" },
  { value: "av_system", label: "سیستم صوتی" },
  { value: "garden", label: "باغ" },
];

// City centers for the default map pin until precise geo is set.
const CITY_GEO: Record<string, { lat: number; lng: number }> = {
  kabul: { lat: 34.53, lng: 69.17 },
  herat: { lat: 34.35, lng: 62.2 },
  mazar: { lat: 36.7, lng: 67.11 },
  kandahar: { lat: 31.62, lng: 65.72 },
  jalalabad: { lat: 34.43, lng: 70.45 },
  kunduz: { lat: 36.73, lng: 68.86 },
};

interface City {
  id: string;
  name: Record<string, string>;
}

export interface EditableHall {
  id: string;
  name: Record<string, string>;
  cityId: string;
  hallType: string;
  capacity?: { total: number; mens?: number; womens?: number };
  hallFeeMinor?: number;
  depositPct?: number;
  instantBooking?: boolean;
  amenities?: string[];
}

export function HallFormDialog({
  orgId,
  open,
  hall,
  onClose,
  onSaved,
}: {
  orgId: string;
  open: boolean;
  /** Present → edit mode; absent → create mode. */
  hall?: EditableHall | null;
  onClose: () => void;
  onSaved: (hallId: string, created: boolean) => void;
}) {
  const isEdit = Boolean(hall);
  const [cities, setCities] = useState<City[]>([]);
  const [nameFa, setNameFa] = useState("");
  const [cityId, setCityId] = useState("kabul");
  const [hallType, setHallType] = useState("standard");
  const [capTotal, setCapTotal] = useState("500");
  const [capMens, setCapMens] = useState("300");
  const [capWomens, setCapWomens] = useState("200");
  const [hallFeeAfn, setHallFeeAfn] = useState("0");
  const [depositPct, setDepositPct] = useState("30");
  const [instant, setInstant] = useState(false);
  const [amenities, setAmenities] = useState<string[]>(["parking", "generator"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, "cities"), orderBy("order"));
    return onSnapshot(q, (snap) => {
      setCities(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<City, "id">) })));
    });
  }, []);

  // Prefill when opening in edit mode (or reset for create).
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (hall) {
      setNameFa(hall.name?.fa ?? "");
      setCityId(hall.cityId ?? "kabul");
      setHallType(hall.hallType ?? "standard");
      setCapTotal(String(hall.capacity?.total ?? 500));
      setCapMens(String(hall.capacity?.mens ?? 0));
      setCapWomens(String(hall.capacity?.womens ?? 0));
      setHallFeeAfn(String(Math.round((hall.hallFeeMinor ?? 0) / 100)));
      setDepositPct(String(hall.depositPct ?? 30));
      setInstant(hall.instantBooking ?? false);
      setAmenities(hall.amenities ?? []);
    } else {
      setNameFa("");
      setCityId("kabul");
      setHallType("standard");
      setCapTotal("500");
      setCapMens("300");
      setCapWomens("200");
      setHallFeeAfn("0");
      setDepositPct("30");
      setInstant(false);
      setAmenities(["parking", "generator"]);
    }
  }, [open, hall]);

  async function submit() {
    setBusy(true);
    setError(null);
    const body = {
      name: { fa: nameFa.trim() },
      cityId,
      geo: CITY_GEO[cityId] ?? CITY_GEO.kabul,
      hallType,
      capacity: {
        total: Number(capTotal),
        mens: Number(capMens) || 0,
        womens: Number(capWomens) || 0,
      },
      amenities,
      shifts: ["MORNING", "EVENING", "FULL_DAY"],
      hallFeeMinor: Math.round(Number(hallFeeAfn) * 100),
      currency: "AFN",
      depositPct: Number(depositPct),
      instantBooking: instant,
    };
    try {
      if (hall) {
        await api(`/orgs/${orgId}/halls/${hall.id}`, { method: "PATCH", body });
        onSaved(hall.id, false);
      } else {
        const result = await api<{ hallId: string }>(`/orgs/${orgId}/halls`, {
          method: "POST",
          body: {
            ...body,
            refundRules: [
              { minDaysBefore: 30, refundPct: 100 },
              { minDaysBefore: 7, refundPct: 50 },
            ],
          },
        });
        onSaved(result.hallId, true);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا در ذخیره تالار");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "ویرایش تالار" : "ثبت تالار جدید"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="نام تالار"
            value={nameFa}
            onChange={(e) => setNameFa(e.target.value)}
            autoFocus
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField select label="شهر" value={cityId} onChange={(e) => setCityId(e.target.value)} fullWidth>
              {cities.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {localized(c.name)}
                </MenuItem>
              ))}
            </TextField>
            <TextField select label="نوع تالار" value={hallType} onChange={(e) => setHallType(e.target.value)} fullWidth>
              {HALL_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="ظرفیت کل" type="number" value={capTotal} onChange={(e) => setCapTotal(e.target.value)} fullWidth />
            <TextField label="مردانه" type="number" value={capMens} onChange={(e) => setCapMens(e.target.value)} fullWidth />
            <TextField label="زنانه" type="number" value={capWomens} onChange={(e) => setCapWomens(e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="کرایه تالار (افغانی، جدا از فی نفر)"
              type="number"
              value={hallFeeAfn}
              onChange={(e) => setHallFeeAfn(e.target.value)}
              fullWidth
            />
            <TextField
              label="بیعانه (٪ از مجموع)"
              type="number"
              value={depositPct}
              onChange={(e) => setDepositPct(e.target.value)}
              fullWidth
            />
          </Stack>
          <FormControlLabel
            control={<Switch checked={instant} onChange={(e) => setInstant(e.target.checked)} />}
            label="رزرو فوری (بدون نیاز به تأیید شما، مستقیم به مرحله بیعانه می‌رود)"
          />
          <Typography variant="subtitle2">امکانات</Typography>
          <FormGroup row>
            {AMENITIES.map((a) => (
              <FormControlLabel
                key={a.value}
                control={
                  <Checkbox
                    checked={amenities.includes(a.value)}
                    onChange={(e) =>
                      setAmenities((prev) =>
                        e.target.checked ? [...prev, a.value] : prev.filter((x) => x !== a.value),
                      )
                    }
                  />
                }
                label={a.label}
              />
            ))}
          </FormGroup>
          <Typography variant="caption" color="text.secondary">
            شرایط لغو پیش‌فرض: تا ۳۰ روز قبل ۱۰۰٪، تا ۷ روز قبل ۵۰٪ بازپرداخت بیعانه — بعداً قابل ویرایش است.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>انصراف</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || nameFa.trim().length < 2 || Number(capTotal) <= 0}
        >
          {isEdit ? "ذخیره تغییرات" : "ثبت تالار"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function MenuFormDialog({
  orgId,
  hallId,
  onClose,
  onCreated,
}: {
  orgId: string;
  hallId: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [nameFa, setNameFa] = useState("");
  const [priceAfn, setPriceAfn] = useState("");
  const [courses, setCourses] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!hallId) return null;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api(`/orgs/${orgId}/halls/${hallId}/menus`, {
        method: "POST",
        body: {
          name: { fa: nameFa.trim() },
          pricePerGuestMinor: Math.round(Number(priceAfn) * 100),
          courses: courses
            .split("،")
            .flatMap((s) => s.split(","))
            .map((s) => s.trim())
            .filter(Boolean),
        },
      });
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا در ثبت منو");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>منوی جدید (قیمت فی نفر)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="نام منو"
            placeholder="مثلاً: منوی عادی"
            value={nameFa}
            onChange={(e) => setNameFa(e.target.value)}
            autoFocus
            fullWidth
          />
          <TextField
            label="قیمت فی نفر (افغانی)"
            type="number"
            value={priceAfn}
            onChange={(e) => setPriceAfn(e.target.value)}
            fullWidth
          />
          <TextField
            label="غذاها (با ، جدا کنید)"
            placeholder="قابلی، کباب، سلاته، نوشابه"
            value={courses}
            onChange={(e) => setCourses(e.target.value)}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>انصراف</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || nameFa.trim().length < 2 || Number(priceAfn) <= 0}
        >
          ثبت منو
        </Button>
      </DialogActions>
    </Dialog>
  );
}
